import { useMemo } from 'react';
import { eachDayOfInterval, parseISO, differenceInDays } from 'date-fns';
import type { Bounds, Calendar, CalendarColumn } from './types';
import { calculateBounds, dayScale, weekScale, monthScale, dateToInterval } from './calc';

import { usePlanMeta } from '../../state/plan';
import { DAY_WIDTH } from './const';
import type { PlanResolution } from '../../types';
import type { Interval } from 'core/types';

const PXGAP = 1;

const scaleFactoryMap: Record<PlanResolution, (interval: Interval, dayWidth: number) => CalendarColumn[]> = {
  DAY: dayScale,
  WEEK: weekScale,
  MONTH: monthScale,
};

export function useCalendar(): Calendar {
  const { date_from, date_to, plan_resolution } = usePlanMeta();

  return useMemo(() => {
    const dayWidth = DAY_WIDTH[plan_resolution];
    const interval = {
      start: parseISO(date_from),
      end: parseISO(date_to),
    };

    const scale: CalendarColumn[] = scaleFactoryMap[plan_resolution](interval, DAY_WIDTH[plan_resolution]);

    const days: CalendarColumn[] = calculateBounds(
      eachDayOfInterval(interval).map((date) => {
        return {
          start: date,
          end: date,
          width: dayWidth,
        };
      }),
    );

    const numDays = days.length - 1;

    function pixelToDate(x: number): Date {
      let dayIndex = Math.floor(x / (dayWidth + PXGAP));
      if (dayIndex < 0) {
        dayIndex = 0;
      } else if (dayIndex > numDays) {
        dayIndex = numDays;
      }

      return days[dayIndex].start;
    }

    function intervalToBounds({ start, end }: Interval): Bounds {
      const left = differenceInDays(start, interval.start) * (dayWidth + PXGAP);
      const width = (differenceInDays(end, start) + 1) * (dayWidth + PXGAP);
      const right = left + width;
      return {
        left,
        width,
        right,
      };
    }

    function dateToInterval_(date: Date): Interval {
      return dateToInterval(date, plan_resolution, interval);
    }

    return {
      resolution: plan_resolution,
      scale,
      dayWidth,
      interval,
      days,
      width: days[numDays].right,
      pixelToDate,
      intervalToBounds,
      dateToInterval: dateToInterval_,
    };
  }, [date_from, date_to, plan_resolution]);
}
